import React, {useCallback, useMemo} from 'react'
import {Marker} from 'react-leaflet'
import {generatePath} from 'react-router'
import {useHistory} from 'react-router-dom'
import {Routes} from '../../common/Routes'
import {slottsskogenPosition} from '../../common/utils/slottsskogenPosition'
import {GenericMap, ZOOM_MANY_MARKERS, ZOOM_ONE_MARKER} from '../location/map/GenericMap'
import {ActivityAreaMarker} from './types/activityAreaMarker'
import {getActivityAreaMarkerIcon} from './utils/getActivityAreaMarker'
import L from 'leaflet'

interface Props {
  activityAreaMarkers: ActivityAreaMarker[]
  selectedId?: string
}

export const ActivityAreaMap: React.FC<Props> = ({activityAreaMarkers, selectedId}) => {
  const history = useHistory()

  const {initialPosition, zoom} = useMemo(() => {
    if (activityAreaMarkers.length > 0) {
      const first = activityAreaMarkers[0].coordinates
      return {
        initialPosition: [first.lat, first.long] as [number, number],
        zoom: activityAreaMarkers.length > 1 ? ZOOM_MANY_MARKERS : ZOOM_ONE_MARKER,
      }
    }
    return {initialPosition: slottsskogenPosition, zoom: ZOOM_ONE_MARKER}
  }, [activityAreaMarkers])

  const preparedMarkers = useMemo(() => {
    return activityAreaMarkers.map((m) => ({
      id: m.id,
      position: [m.coordinates.lat, m.coordinates.long] as [number, number],
      iconKey: `${m.type}:${m.active ? 1 : 0}`,
      data: m,
      isSelected: m.id === selectedId,
    }))
  }, [activityAreaMarkers, selectedId])

  const iconCache = useMemo(() => new Map<string, ReturnType<typeof getActivityAreaMarkerIcon>>(), [])
  const getIcon = useCallback(
    (marker: ActivityAreaMarker, key: string) => {
      const cached = iconCache.get(key)
      if (cached) return cached
      const created = getActivityAreaMarkerIcon(marker)
      iconCache.set(key, created)
      return created
    },
    [iconCache],
  )

  // Larger crosshair DivIcon
  const crosshairCache = useMemo(() => new Map<string, L.DivIcon>(), [])
  const getCrosshairIcon = useCallback(
    (sizePx = 140, color = '#004D40') => {
      const key = `${sizePx}:${color}`
      const cached = crosshairCache.get(key)
      if (cached) return cached

      const stroke = color
      const fill = color
      const half = sizePx / 2
      const rOuter = sizePx * 0.4
      const rInner = sizePx * 0.16
      const arm = sizePx * 0.22
      const armGap = sizePx * 0.08

      const svg = `
      <svg width="${sizePx}" height="${sizePx}" viewBox="0 0 ${sizePx} ${sizePx}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="crosshairGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <!-- outer ring -->
        <circle cx="${half}" cy="${half}" r="${rOuter}" fill="${fill}15" stroke="${stroke}" stroke-width="3" filter="url(#crosshairGlow)"/>
        <!-- inner cutout -->
        <circle cx="${half}" cy="${half}" r="${rInner}" fill="transparent" stroke="${stroke}33" stroke-width="2" />

        <!-- crosshair arms -->
        <line x1="${half - rOuter + arm}" y1="${half}" x2="${half - rOuter + arm + armGap}" y2="${half}" stroke="${stroke}" stroke-width="3" />
        <line x1="${half + rOuter - arm}" y1="${half}" x2="${half + rOuter - arm - armGap}" y2="${half}" stroke="${stroke}" stroke-width="3" />
        <line x1="${half}" y1="${half - rOuter + arm}" x2="${half}" y2="${half - rOuter + arm + armGap}" stroke="${stroke}" stroke-width="3" />
        <line x1="${half}" y1="${half + rOuter - arm}" x2="${half}" y2="${half + rOuter - arm - armGap}" stroke="${stroke}" stroke-width="3" />

        <!-- tiny center dot -->
        <circle cx="${half}" cy="${half}" r="${Math.max(2, sizePx * 0.02)}" fill="${fill}" />
      </svg>
    `

      const divIcon = L.divIcon({
        className: 'aa-crosshair-icon',
        html: svg,
        iconSize: [sizePx, sizePx],
        iconAnchor: [half, half],
      })

      crosshairCache.set(key, divIcon)
      return divIcon
    },
    [crosshairCache],
  )

  const onClickMarker = useCallback(
    (id: string) => {
      window.dispatchEvent(new Event('activity-area-flash'))
      history.push(generatePath(Routes.ACTIVITY_AREA, {id}))
    },
    [history],
  )

  return (
    <GenericMap initialPosition={initialPosition} zoom={zoom}>
      {preparedMarkers.map((m) => (
        <React.Fragment key={m.id}>
          {m.isSelected && (
            <Marker
              position={m.position}
              icon={getCrosshairIcon(140, '#004D40')} // much larger
              zIndexOffset={900}
              interactive={false}
            />
          )}
          <Marker
            icon={getIcon(m.data, m.iconKey)}
            position={m.position}
            zIndexOffset={m.isSelected ? 1000 : 0}
            eventHandlers={{
              click: () => onClickMarker(m.id),
            }}
          />
        </React.Fragment>
      ))}
    </GenericMap>
  )
}
